import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
  FormControl,
  Button,
  Box,
  Switch,
  FormControlLabel,
  Container,
  Alert,
  Card,
  CardContent,
  TextField,
  Autocomplete,
} from "@mui/material";

export default function TubingCalc() {
  const [dataRaw, setDataRaw] = useState([]); // Stores raw data (loaded once)
  const [data, setData] = useState([]); // Stores processed data for filtering
  const [filteredData, setFilteredData] = useState([]);
  const [pendingFilteredData, setPendingFilteredData] = useState([]); // Holds real-time filtering results
  const [showTable, setShowTable] = useState(false);
  const [formulation, setFormulation] = useState("");
  const [tubingSize, setTubingSize] = useState("");
  const [idSize, setIdSize] = useState("");
  const [odSize, setOdSize] = useState("");
  const [wallSize, setWallSize] = useState("");
  const [useMetric, setUseMetric] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    // Dynamically inject base tag in <head>
    const referrer = document.referrer;
    let hostname = "";
    try {
      hostname = referrer ? new URL(referrer).hostname : "www.coleparmer.com";
    } catch (error) {
      hostname = "www.coleparmer.com";
    }

    const base = document.createElement("base");
    base.href = `https://${hostname}`;
    base.target = "_parent";
    document.head.appendChild(base);
  }, []);

  useEffect(() => {
    //prod or local
    fetch(
      new URL(
        "/tubing/Tubing Selection Size Phase 2.xlsx",
        window.location.origin
      )
    )
    //staging
    // fetch(
    //   new URL(
    //     "/Tubing Selection Size Phase 2.xlsx",
    //     window.location.origin
    //   )
    // )
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheet = XLSX.utils.sheet_to_json(
          workbook.Sheets[workbook.SheetNames[0]],
          { header: 1 }
        );
        const formattedData = sheet.slice(2).map((row) => {
          const partNumbers = [];

          for (let i = 8; i < row.length; i += 3) {
            if (row[i]) {
              partNumbers.push({
                number: row[i],
                formulation: row[i + 1],
                url: row[i + 2],
              });
            }
          }

          return {
            Formulation: row[0],
            TubingSize: row[1],
            ID_Metric: row[3],
            ID_Imperial: row[2],
            OD_Metric: row[5],
            OD_Imperial: row[4],
            Wall_Metric: row[7],
            Wall_Imperial: row[6],
            PartNumbers: partNumbers,
          };
        });

        setDataRaw(formattedData);
      });
  }, []);

  // ✅ Update the `data` dynamically based on metric selection
  useEffect(() => {
    setFormulation(formulation || "");
    setIdSize("");
    setOdSize("");
    setWallSize("");

    const transformedData = dataRaw.map((row) => ({
      ...row,
      ID: useMetric ? row.ID_Metric : row.ID_Imperial,
      OD: useMetric ? row.OD_Metric : row.OD_Imperial,
      Wall: useMetric ? row.Wall_Metric : row.Wall_Imperial,
    }));
    setData(transformedData);
    setShowTable(false);
  }, [useMetric, dataRaw]);

  // ✅ Update pendingFilteredData dynamically when filters change
  useEffect(() => {
    let result = data;
    if (formulation)
      result = result.filter((row) => row.Formulation === formulation);
    if (tubingSize)
      if (odSize) result = result.filter((row) => row.OD === odSize);
    if (wallSize) result = result.filter((row) => row.Wall === wallSize);

    setPendingFilteredData(result); // Store it in pending state
  }, [formulation, tubingSize, idSize, odSize, wallSize, data]);

  // ✅ Get filtered dropdown options based on selected filters
const getFilteredOptions = (key) => {
  let keyImperial, keyMetric;

  switch (key) {
    case "ID":
      keyImperial = "ID_Imperial";
      keyMetric = "ID_Metric";
      break;
    case "OD":
      keyImperial = "OD_Imperial";
      keyMetric = "OD_Metric";
      break;
    case "Wall":
      keyImperial = "Wall_Imperial";
      keyMetric = "Wall_Metric";
      break;
    default:
      keyImperial = null;
      keyMetric = null;
  }

  const filtered = data.filter((row) =>
    (!formulation || row.Formulation === formulation) &&
    (!tubingSize || row.TubingSize === tubingSize) &&
    (!idSize || row.ID === idSize) &&
    (!odSize || row.OD === odSize) &&
    (!wallSize || row.Wall === wallSize)
  );

  let options;

  if (useMetric && keyImperial && keyMetric) {
    // Deduplicate based on Imperial values, keep one metric per unique imperial
    const imperialToMetricMap = new Map();
    for (const row of filtered) {
      const imperialVal = row[keyImperial];
      const metricVal = row[keyMetric];
      if (imperialVal && metricVal && !imperialToMetricMap.has(imperialVal)) {
        imperialToMetricMap.set(imperialVal, metricVal);
      }
    }
    // Sort numerically
    options = [...imperialToMetricMap.values()].sort((a, b) => parseFloat(a) - parseFloat(b));
  } else {
    options = [...new Set(filtered.map((row) => row[key]))].filter(Boolean);
    if (key === "TubingSize") {
      options = options.filter((value) => value !== "NA");
    }

    // For imperial, sort numerically if possible
    options.sort((a, b) => {
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return a.localeCompare(b);
    });
  }

  return options;
};
 

  // Handle dropdown changes for all fields independently
  const handleFormulationChange = (newValue) => {
    setFormulation(newValue || "");
  };
  const handleTubingSizeChange = (newValue) => {
    setTubingSize(newValue || "");
  };
  const handleIdSizeChange = (newValue) => {
    setIdSize(newValue || "");
  };
  const handleOdSizeChange = (newValue) => {
    setOdSize(newValue || "");
  };
  const handleWallSizeChange = (newValue) => {
    setWallSize(newValue || "");
  };

  const handleReset = () => {
    setFormulation("");
    setTubingSize("");
    setIdSize("");
    setOdSize("");
    setWallSize("");
    setFilteredData([]);
    setUseMetric(false);
    setShowTable(false);
  };

  // ✅ Update filteredData only when "Apply Filters" is clicked
  const handleFilter = () => {
    setErrorMessage("");
    if (!formulation) {
      setErrorMessage("Formulation is required.");
      setShowTable(false);
      return;
    }
    if (!(tubingSize || (idSize && odSize))) {
      setErrorMessage("Please select either Tubing Size or both ID and OD.");
      setShowTable(false);
      return;
    }

    // Apply all selected filters for table output
    let output = pendingFilteredData;
    if (formulation) {
      output = output.filter((row) => row.Formulation === formulation);
    }
    if (tubingSize) {
      output = output.filter((row) => row.TubingSize === tubingSize);
    }
    if (idSize) {
      output = output.filter((row) => row.ID === idSize);
    }
    if (odSize) {
      output = output.filter((row) => row.OD === odSize);
    }
    if (wallSize) {
      output = output.filter((row) => row.Wall === wallSize);
    }
    // 🔄 Flatten and sort by formulation
    const flatOutput = output.flatMap((row) =>
      row.PartNumbers.map((pn) => ({
        partNumber: pn.number,
        partUrl: pn.url,
        formulation: pn.formulation || row.Formulation,
        tubingSize: row.TubingSize,
        id: row.ID,
        od: row.OD,
        wall: row.Wall,
      }))
    );

    flatOutput.sort((a, b) => a.formulation.localeCompare(b.formulation));

    // 🆕 Store sorted flattened list
    setFilteredData(flatOutput);

    setShowTable(true);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh", // Full height of viewport
        overflow: "hidden", // Prevents scrolling issues
        fontFamily: "Lato, sans-serif", // Updated font
      }}
    >
      {/* Main Content */}
      {/* <Box sx={{ flex: 1, overflow: "auto", paddingX: 3, marginTop: 3 }}> */}
      <Box
        sx={{
          mb: 2,
          p: 3, // Increased padding
          mt: 3, // Added top margin
          borderRadius: 2, // Rounded corners
          fontFamily: "Lato, sans-serif",
        }}
      >
        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 2, fontFamily: "Lato, sans-serif" }}
          >
            {errorMessage}
          </Alert>
        )}

        {/* Filters */}
        {/* <Card
          sx={{
            mb: 2,
            p: 2,
            //boxShadow: 3,
            //borderRadius: 2,
            fontFamily: "Lato, sans-serif",
          }}
        > */}
        {/* <CardContent> */}
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(auto-fit, minmax(150px, 1fr))", // Mobile: Auto adjust
            sm: "repeat(2, 1fr)", // Tablets: 2 columns
            md: "repeat(3, 1fr)", // Medium screens: 3 columns
            lg: "repeat(5, 1fr)", // Large screens: 5 columns
          }}
          gap={2}
          mb={2}
        >
          <FormControl fullWidth required>
            <Autocomplete
              options={getFilteredOptions("Formulation")}
              value={formulation}
              onChange={(event, newValue) => handleFormulationChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Formulation"
                  size="small"
                  sx={{ fontFamily: "Lato, sans-serif" }}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <Autocomplete
              options={getFilteredOptions("TubingSize")}
              value={tubingSize}
              onChange={(event, newValue) => handleTubingSizeChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tubing Size"
                  size="small"
                  sx={{ fontFamily: "Lato, sans-serif" }}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <Autocomplete
              options={getFilteredOptions("ID")}
              value={idSize}
              onChange={(event, newValue) => handleIdSizeChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="ID Size"
                  size="small"
                  sx={{ fontFamily: "Lato, sans-serif" }}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <Autocomplete
              options={getFilteredOptions("OD")}
              value={odSize}
              onChange={(event, newValue) => handleOdSizeChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="OD Size"
                  size="small"
                  sx={{ fontFamily: "Lato, sans-serif" }}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <Autocomplete
              options={getFilteredOptions("Wall")}
              value={wallSize}
              onChange={(event, newValue) => handleWallSizeChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Wall Size"
                  size="small"
                  sx={{ fontFamily: "Lato, sans-serif" }}
                />
              )}
            />
          </FormControl>
        </Box>

        {/* Toggle Button */}
        <Box
          display="flex"
          justifyContent="flex-start"
          mb={2}
          sx={{ fontFamily: "Lato, sans-serif" }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={useMetric}
                onChange={() => setUseMetric(!useMetric)}
              />
            }
            label="Use Metric (mm)"
          />
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          sx={{ fontFamily: "Lato, sans-serif" }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleReset}
            sx={{
              fontWeight: "bold",
              color: "#06c",
              borderColor: "#86979e",
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleFilter}
            sx={{
              fontWeight: "bold",
              backgroundColor: "#0066CC",
              "&:hover": { backgroundColor: "#005bb5" },
            }}
          >
            Apply Filters
          </Button>
        </Box>
        {/* </CardContent> */}
        {/* </Card> */}

        {/* Table (Scrollable) */}
        {showTable && (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ mt: 3, fontFamily: "Lato, sans-serif" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}
                  >
                    <strong>Part Number</strong>
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}
                  >
                    <strong>Formulation</strong>
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}
                  >
                    <strong>Tubing Size</strong>
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}
                  >
                    <strong>ID</strong>
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}
                  >
                    <strong>OD</strong>
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}
                  >
                    <strong>Wall</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}>
                      {row.partUrl ? (
                        <Link
                          style={{ textDecoration: "none" }}
                          href={row.partUrl}
                          rel="noopener noreferrer"
                        >
                          {row.partNumber}
                        </Link>
                      ) : (
                        row.partNumber
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}>
                      {row.formulation}
                    </TableCell>
                    <TableCell sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}>
                      {row.tubingSize}
                    </TableCell>
                    <TableCell sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}>
                      {row.id}
                    </TableCell>
                    <TableCell sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}>
                      {row.od}
                    </TableCell>
                    <TableCell sx={{ fontSize: "1rem", fontFamily: "Lato, sans-serif" }}>
                      {row.wall}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Container>
  );
}
